import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import type { ColDef } from 'ag-grid-community'
import { useMemo } from 'react'

import { Fee, Product, ProductListItem, ProductStatusType } from '@/api/admin-products-types'
import {
	useDeleteProductGroup,
	useDeleteProductGroupFee,
	useSetProductGroupImage,
	useDeleteProductGroupImage
} from '@/api/api-mutations'
import { preloadGetProductGroup, useGetProductGroup, useListProducts } from '@/api/react-query'
import { AssetImageManager } from '@/components/asset-image-manager'
import { Button } from '@/components/base/button'
import { Container, ContainerHeader } from '@/components/base/container'
import { IconButton } from '@/components/base/icon-button'
import { usePrompt } from '@/components/base/prompt'
import { StatusCell } from '@/components/base/status-cell'
import { Text } from '@/components/base/text'
import { Thumbnail } from '@/components/base/thumbnail'
import { toast } from '@/components/base/toast'
import { SingleColumnPage } from '@/components/page-layout'
import { GenericDetailSkeleton } from '@/components/skeletons/generic-detail-skeleton'
import { DEFAULT_PAGE_SIZE, Pagination } from '@/components/table/pagination'
import { createActionsColumn, Table } from '@/components/table/table'
import { useI18n } from '@/i18n/i18n-react'
import { Pencil } from '@/icons/pencil'
import { Plus } from '@/icons/plus'
import { Trash } from '@/icons/trash'
import { PaginatedSearchSchema } from '@/utils/route-utilities'

import { useProductListColumnDefs } from '../products/-components'

export const Route = createFileRoute('/_authenticated/_general/product-groups/$groupUuid')({
	component: RouteComponent,
	pendingComponent: GenericDetailSkeleton,
	validateSearch: PaginatedSearchSchema,
	loader: async ({ params: { groupUuid }, context }) =>
		await preloadGetProductGroup(context, {
			path: { groupUuid }
		}),
	staticData: {
		crumb: ({ loaderData, context: { L } }) => loaderData?.name
	}
})

function RouteComponent() {
	const { groupUuid } = Route.useParams()
	const { limit = DEFAULT_PAGE_SIZE, offset, q } = Route.useSearch()
	const { L } = useI18n()
	const navigate = useNavigate()
	const { data } = useGetProductGroup({ groupUuid })
	const deleteFee = useDeleteProductGroupFee()
	const deleteProductGroup = useDeleteProductGroup()
	const setImage = useSetProductGroupImage()
	const deleteImage = useDeleteProductGroupImage()
	const products = useListProducts({
		groupUuids: [groupUuid],
		limit,
		offset,
		q
	})

	const prompt = usePrompt()

	const handleDelete = async () => {
		const res = await prompt({
			title: L.ProductGroup.Delete.Title(),
			description: L.ProductGroup.Delete.Description({ name: data?.name ?? '' }),
			confirmText: L.General.Delete(),
			cancelText: L.General.Cancel()
		})

		if (!res) {
			return
		}

		const result = await deleteProductGroup({ path: { groupUuid } })
		if (result.isSuccess) {
			toast.success(L.ProductGroup.Delete.SuccessToast({ name: data?.name ?? '' }))
			navigate({ to: '/product-groups' })
		} else {
			toast.error((result.response as any)?.message ?? L.Error.SomethingWentWrong())
		}
	}

	const columnDefs = useMemo<ColDef<Fee>[]>(
		() => [
			{
				headerName: L.Fields.Name(),
				field: 'name',
				flex: 1
			},
			{
				headerName: L.Fields.Amount(),
				type: 'currency',
				valueGetter: ({ data }) => {
					if (!data) {
						return ''
					}
					if (data.prices.length > 1) {
						return L.General.Multiple()
					}
					const price = data.prices[0]
					if (price.priceInfo.variablePrice) {
						return L.General.Variable()
					}
					return price.priceInfo.amount
				}
			},
			{
				headerName: L.Fields.TaxRate(),
				valueGetter: ({ data }) => {
					if (!data) {
						return ''
					}
					if (data.prices.length > 1) {
						return L.General.Multiple()
					}
					const price = data.prices[0]
					return price.priceInfo.taxRateName
				}
			},
			createActionsColumn([
				{
					icon: <Trash />,
					onClick: async row => {
						const res = await prompt({
							title: L.ProductGroup.Fees.Delete.Title(),
							description: L.ProductGroup.Fees.Delete.Description({
								name: row.name
							}),
							confirmText: L.General.Delete(),
							cancelText: L.General.Cancel()
						})

						if (!res) {
							return
						}

						await deleteFee({
							path: { groupUuid, feeUuid: row.uuid }
						})
					}
				}
			])
		],
		[L]
	)

	const productsColumnDefs = useProductListColumnDefs()

	return (
		// The group list owns the left column; this renders into its outlet on the right.
		<SingleColumnPage hasOutlet>
			<Container>
				<ContainerHeader
					heading={data?.name ?? ''}
					rightSide={
						<Link from={Route.fullPath} to='edit'>
							<IconButton tooltipText={L.General.Edit()}>
								<Pencil />
							</IconButton>
						</Link>
					}
					actions={[
						{
							actions: [
								{
									label: L.General.Delete(),
									icon: <Trash />,
									onClick: handleDelete
								}
							]
						}
					]}
				/>
			</Container>
			<AssetImageManager
				image={data?.image}
				labels={{
					title: L.ProductGroup.Image.Title(),
					emptyState: L.ProductGroup.Image.EmptyState(),
					upload: L.ProductGroup.Image.Upload(),
					chooseFromLibrary: L.ProductGroup.Image.ChooseFromLibrary(),
					remove: L.ProductGroup.Image.Remove(),
					libraryPickerTitle: L.ProductGroup.Image.LibraryPickerTitle(),
					libraryPickerEmpty: L.ProductGroup.Image.LibraryPickerEmpty(),
					libraryPickerAllAttached: L.ProductGroup.Image.LibraryPickerAllAttached(),
					uploadFailedToast: L.ProductGroup.Image.UploadFailedToast()
				}}
				onSetAsset={async assetUuid => {
					await setImage({ path: { groupUuid }, body: { assetUuid } })
					toast.success(L.ProductGroup.Image.SetSuccessToast())
				}}
				onRemove={async () => {
					await deleteImage({ path: { groupUuid } })
					toast.success(L.ProductGroup.Image.RemoveSuccessToast())
				}}
			/>
			<Container>
				<ContainerHeader
					heading={L.ProductGroup.Fees.Title()}
					rightSide={
						<Link from={Route.fullPath} to='fees/create'>
							<IconButton tooltipText={L.ProductGroup.Fees.AddFee()}>
								<Plus />
							</IconButton>
						</Link>
					}
				/>
				{data && data.fees && data.fees.length > 0 ? (
					<Table<Fee>
						data={data?.fees}
						columnDefs={columnDefs}
						className='h-[150px] min-h-[150px]'
						suppressCellFocus
						columnUtilities={{
							getCurrencyCode: data => data.prices[0].currencyCode
						}}
						rowToRouterConfig={row => ({
							to: '/product-groups/$groupUuid/fees/$feeUuid/edit',
							params: { groupUuid, feeUuid: row.uuid }
						})}
					/>
				) : (
					<div className='flex flex-col items-center justify-center gap-y-4 bg-ui-bg-subtle pb-8 pt-6'>
						<div className='flex flex-col items-center'>
							<Text size='small' leading='compact' weight='plus' className='text-ui-fg-subtle'>
								{L.ProductGroup.Fees.NoFees()}
							</Text>
						</div>
						<Link from={Route.fullPath} to='fees/create'>
							<Button variant='secondary' size='small'>
								{L.ProductGroup.Fees.AddFee()}
							</Button>
						</Link>
					</div>
				)}
			</Container>
			<Container className='flex flex-col'>
				<ContainerHeader
					heading={L.ProductGroup.Products.Title()}
					rightSide={
						<Link from={Route.fullPath} to='products/add'>
							<IconButton tooltipText={L.ProductGroup.Products.Add.Title()}>
								<Plus />
							</IconButton>
						</Link>
					}
				/>
				<div className='min-w-0 overflow-x-auto'>
					<Table<ProductListItem>
						className='w-full min-w-[600px]'
						data={products.data?.products}
						columnDefs={productsColumnDefs}
						animateRows
						domLayout='autoHeight'
						suppressCellFocus
						rowToRouterConfig={row => ({
							to: '/products/$productUuid',
							params: { productUuid: row.uuid }
						})}
					/>
				</div>
				<div className='px-3 py-2 sm:px-6 sm:py-4'>
					<Pagination totalCount={products.data?.count} />
				</div>
			</Container>
		</SingleColumnPage>
	)
}
